import React, {useContext, useEffect, useState} from "react";
import {View, TouchableOpacity} from "react-native";
import {useDispatch, useSelector} from "react-redux";
import {GetOrderItemDetailsRequest} from "../../../model/GetOrderItemDetailsRequest";
import {getOrderItemDetails} from "../../../actions/orderActions";
import PtText from "../../core/pt-text/pt-text";
import PtIcon from "../../core/pt-icon/pt-icon";
import {getTranslation} from "../../../helpers/getTranslation";
import PtActivityIndicator from "../../core/pt-activity-indicator/pt-activity-indicator";
import PtCallout from "../../core/pt-callout/pt-callout";
import PtOrderStatusIndicator from "./../pt-order-status-indicator/pt-order-status-indicator";
import {PtOrderItemDetailsProps} from './pt-order-item-details-props';
import {styles} from './pt-order-item-details-styles';
import UserContext from "../../../helpers/UserContext";

export default function PtOrderItemDetails(props: PtOrderItemDetailsProps) {

  const {userData, updateUserData} = useContext(UserContext);

  /** labels */
  const noDocumentsLabel = props.noDocumentsLabel || getTranslation('order.history.delivery.details.no.documents.found.label') || 'No documents found';
  const deliveryNumberLabel = props.deliveryNumberLabel || getTranslation('order.history.delivery.details.delivery.number.label') || 'Delivery number';
  const estimatedDeliveryDateLabel = props.estimatedDeliveryDateLabel || getTranslation('order.history.delivery.details.estimated.delivery.date.label') || 'Est. delivery date';
  const dispatchDateLabel = props.dispatchDateLabel || getTranslation('order.history.delivery.details.dispatch.date.label') || 'Dispatch date';
  const arrivalDateLabel = props.arrivalDateLabel || getTranslation('order.history.delivery.details.arrival.date.label') || 'Arrival date';
  const deliveryDateLabel = props.deliveryDateLabel || getTranslation('order.history.delivery.details.delivery.date.label') || 'Delivery date';
  const deliveredQtyLabel = props.deliveredQuantityLabel || getTranslation('order.history.delivery.details.delivered.qty.label') || 'Delivered qty';
  const carrierLabel = props.carrierLabel || getTranslation('order.history.delivery.details.carrier.label') || 'Carrier';
  const trackingIdLabel = props.trackingIdLabel || getTranslation('order.history.delivery.details.tracking.id.label') || 'Tracking #';
  const errorMessage = props.errorMessage || getTranslation('order.history.error.message') || 'Error occurred';

  /** config */
  const showItemDetailsLabel = props.showItemDetailsLabel || getTranslation('order.history.product.show.delivery.details.label') || 'Show delivery details';
  const hideItemDetailsLabel = props.hideItemDetailsLabel || getTranslation('order.history.product.hide.delivery.details.label') || 'Hide delivery details';
  const hideTrackingId = props.hideTrackingId || false;

  /** data */
  const order = props.order;
  const orderItem = props.orderItem;

  /** states */
  const [itemDetailsData, setItemDetailsData] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [collectingData, setCollectingData] = useState(false);
  const [detailsCollected, setDetailsCollected] = useState(false);
  const [errorInCollection, setErrorInCollection] = useState(false);

  /** request stuff */
  const PTBaseSiteUID = props.baseSiteUID || userData.baseSiteUID;
  const PTSalesAreaDependantCustomerUid = props.salesAreaDependantCustomerUid || userData.salesAreaDependantCustomerUid;
  const currentLanguage = useSelector((state:any) => state.translationsReducer.currentLanguage);
  const dispatch = useDispatch();

  const shouldCollectDetailsData = () => {
    return !detailsCollected && showDetails;
  }

  useEffect(() => {
    if(shouldCollectDetailsData()){
      setCollectingData(true);
      const itemDetailsRequest = new GetOrderItemDetailsRequest(order.orderNumber, orderItem.entryNumber, order.scenarioId);
      getOrderItemDetails(itemDetailsRequest, dispatch, PTBaseSiteUID, PTSalesAreaDependantCustomerUid, currentLanguage)
        .then((res) => {
          console.log('u then od get details request, result = ', res);
          setItemDetailsData(res);
        })
        .catch((error) => {
          console.log('error in getOrderItemDetails, error = ', error);
          setErrorInCollection(true);
        })
        .finally(() => {
          setDetailsCollected(true);
          setCollectingData(false);
        });
    }
  },[shouldCollectDetailsData()]);

  const toggleOrderItemDetails = () => {
    setShowDetails(!showDetails);
  }

  const hasDocuments = () => {
    if(detailsCollected && Array.isArray(itemDetailsData?.deliveryDetails)) {
      const dd = itemDetailsData?.deliveryDetails
        .filter(orderItemDetail => orderItemDetail.deliveryDetailItems && Array.isArray(orderItemDetail.deliveryDetailItems))
      return dd.length > 0
    }
    return false;
  }

  /** renders */
  const renderStatus = (deliveryDetailItem:any) => {
    return (
      <View style={[styles.deliveryDetailItemStatus]}>
        <View>
          <PtOrderStatusIndicator statusCode={deliveryDetailItem.statusCode} />
        </View>
      </View>
    )
  }

  const renderEstimatedDeliveryDate = (deliveryDetailItem:any) => {
    return deliveryDetailItem.estimatedArrivalDate ?
      <View style={[styles.sectionContainer]}>
        <View style={[styles.sectionLabel]}>
          <PtText style={[styles.sectionLabelText]}>
            {estimatedDeliveryDateLabel}
          </PtText>
        </View>
        <View style={[styles.sectionValue]}>
          <PtText style={[styles.sectionValueText]}>
            {deliveryDetailItem.estimatedArrivalDate}
          </PtText>
        </View>
      </View>
      : null
  }

  const renderDispatchDate = (deliveryDetailItem:any) => {
    return deliveryDetailItem.dispatchDate ?
      <View style={[styles.sectionContainer]}>
        <View style={[styles.sectionLabel]}>
          <PtText style={[styles.sectionLabelText]}>
            {dispatchDateLabel}
          </PtText>
        </View>
        <View style={[styles.sectionValue]}>
          <PtText style={[styles.sectionValueText]}>
            {deliveryDetailItem.dispatchDate}
          </PtText>
        </View>
      </View>
      : null
  }

  const renderArrivalDate = (deliveryDetailItem:any) => {
    return deliveryDetailItem.arrivalDate ?
      <View style={[styles.sectionContainer]}>
        <View style={[styles.sectionLabel]}>
          <PtText style={[styles.sectionLabelText]}>
            {arrivalDateLabel}
          </PtText>
        </View>
        <View style={[styles.sectionValue]}>
          <PtText style={[styles.sectionValueText]}>
            {deliveryDetailItem.arrivalDate}
          </PtText>
        </View>
      </View>
      : null
  }

  const renderDeliveryDates = (deliveryDetailItem:any) => {
    return deliveryDetailItem.estimatedArrivalDate || deliveryDetailItem.dispatchDate || deliveryDetailItem.arrivalDate ?
      <View>
        {renderEstimatedDeliveryDate(deliveryDetailItem)}
        {renderDispatchDate(deliveryDetailItem)}
        {renderArrivalDate(deliveryDetailItem)}
      </View>
      :
      <View style={[styles.sectionContainer]}>
        <View style={[styles.sectionLabel]}>
          <PtText style={[styles.sectionLabelText]}>
            {deliveryDateLabel}
          </PtText>
        </View>
        <View style={[styles.sectionValue]}>
          <PtText style={[styles.sectionValueText]}>
            {deliveryDetailItem.deliveryDate}
          </PtText>
        </View>
      </View>
  }

  const renderDeliveredQuantity = (deliveryDetailItem:any) => {
    return (
      <View style={[styles.sectionContainer]}>
        <View style={[styles.sectionLabel]}>
          <PtText style={[styles.sectionLabelText]}>
            {deliveredQtyLabel}
          </PtText>
        </View>
        <View style={[styles.sectionValue]}>
          <PtText style={[styles.sectionValueText]}>
            {deliveryDetailItem.deliveredQuantity}
          </PtText>
        </View>
      </View>
    )
  }

  const renderCarrier = (deliveryDetailItem:any) => {
    return deliveryDetailItem.carrierName ?
      <View style={[styles.sectionContainer]}>
        <View style={[styles.sectionLabel]}>
          <PtText style={styles.sectionLabelText}>
            {carrierLabel}
          </PtText>
        </View>
        <View style={[styles.sectionValue]}>
          <PtText style={[styles.sectionValueText]}>
            {deliveryDetailItem.carrierName}
          </PtText>
        </View>
      </View>
      : null
  }

  const renderTrackingId = (deliveryDetailItem:any) => {
    return !hideTrackingId && deliveryDetailItem?.trackingDetails ?
      <View style={[styles.sectionContainer]}>
        <View style={[styles.sectionLabel]}>
          <PtText style={[styles.sectionLabelText]}>
            {trackingIdLabel}
          </PtText>
        </View>
        <View style={[styles.sectionValue]}>
          <PtText style={[styles.sectionValueText]}>
            {deliveryDetailItem.trackingDetails.trackingId}
          </PtText>
        </View>
      </View>
      : null
  }

  return (
    <View style={[styles.orderItemDetailsContainer]}>
      <View style={[styles.orderItemDetailsHeader]}>
        <TouchableOpacity onPress={() => toggleOrderItemDetails()}>
          <PtText>
            {showDetails ?
              <PtText style={[styles.orderItemDetailsHeaderText]}>
                <PtIcon iconName={'up'} style={[styles.orderItemDetailsHeaderText]}/>{hideItemDetailsLabel}
              </PtText>
              :
              <PtText style={[styles.orderItemDetailsHeaderText]}>
                <PtIcon iconName={'down'} style={[styles.orderItemDetailsHeaderText]}/>{showItemDetailsLabel}
              </PtText>
            }
          </PtText>
        </TouchableOpacity>
      </View>
      {showDetails ?
        !errorInCollection ?
          <>
            <PtActivityIndicator show={collectingData}/>
            <View style={[styles.flyoutContainer]}>
              { hasDocuments() ?
                <View>
                  {itemDetailsData?.deliveryDetails?.map((deliveryDetail:any, idx:number) => (
                    <View style={[styles.deliveryDetailsContent]} key={`delivery-details-content-${idx}`}>
                      {deliveryDetail?.deliveryDetailItems?.map((deliveryDetailItem:any, ind:number) => (
                        <View style={[styles.deliveryDetailItemContainer]} key={`delivery-detail-item-${ind}`}>
                          {renderStatus(deliveryDetailItem)}
                          <View style={[styles.sectionContainer]}>
                            <View style={[styles.sectionLabel]}>
                              <PtText style={[styles.sectionLabelText]}>
                                {deliveryNumberLabel}
                              </PtText>
                            </View>
                            <View style={[styles.sectionValue]}>
                              <PtText style={[styles.sectionValueText]}>
                                {deliveryDetailItem.deliveryNumber}
                              </PtText>
                            </View>
                          </View>
                          {renderDeliveryDates(deliveryDetailItem)}
                          {renderDeliveredQuantity(deliveryDetailItem)}
                          {renderCarrier(deliveryDetailItem)}
                          {renderTrackingId(deliveryDetailItem)}
                        </View>
                      ))}
                    </View>
                  ))}
                </View>
                :
                <View>
                  <PtText style={[styles.noDetailsMessageText]}>
                    {noDocumentsLabel}
                  </PtText>
                </View>
              }
            </View>
          </>
          :
          <PtCallout type={'error'} message={errorMessage}/>
        : null
      }
    </View>
  )
}
